import React, { useState } from 'react';
import {
  Button,
  Card,
  Space,
  Popconfirm,
  Modal,
  Form,
  Input,
  Select,
  Radio,
} from 'antd';
import {
  PlusOutlined,
  MenuOutlined,
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import {
  SortableContainer,
  SortableElement,
  sortableHandle,
} from 'react-sortable-hoc';
import ImgUpload from '@/components/ImgUpload';
import styles from './ConfigArea.less';

// eslint-disable-next-line react/prop-types
const FormImgUpload = ({ value, onChange, maxSize }) => {
  const imgList = value ? [{ url: value, name: 'icon' }] : [];
  return (
    <ImgUpload
      mode="single"
      imgList={imgList}
      setImgList={(list) => {
        onChange?.(list?.[0]?.url || undefined);
      }}
      isSupportGif={true}
      maxSize={maxSize}
    />
  );
};

const DragHandle = sortableHandle(() => (
  <MenuOutlined className={styles.dragHandle} />
));

const SubFunctionItem = SortableElement(
  ({
    item,
    itemIndex,
    groupIndex,
    group,
    onEdit,
    onDelete,
    appModuleTabList,
  }) => {
    return (
      <div className={styles.subItem}>
        <DragHandle />
        <div className={styles.subItemContent}>
          <div className={styles.subItemIcon}>
            {item.tabIcon ? (
              <img src={item.tabIcon} alt="icon" />
            ) : (
              <div className={styles.placeholderIcon} />
            )}
          </div>
          <div className={styles.subItemInfo}>
            <div className={styles.subItemTitle}>{item.tabName}</div>
            <div className={styles.subItemDesc}>
              {item.jumpType === 1
                ? 'H5'
                : item.jumpType === 3
                ? '小程序'
                : '已有功能'}
              {/* {' - '}
              {appModuleTabList?.find((p) => p.tabCode === item.tabCode)?.tabName ||
                item.h5Url || item.tabCode || item.miniProgramPath ||
                '未配置'} */}
            </div>
          </div>
        </div>
        <Space className={styles.subItemActions}>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => onEdit(groupIndex, itemIndex, item)}
          />
          {group.styleType !== 1 && (
            <Popconfirm
              title="确定要删除此子功能吗？"
              onConfirm={() => onDelete(groupIndex, itemIndex)}
            >
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      </div>
    );
  },
);

const SubFunctionList = SortableContainer(
  ({ items, groupIndex, group, onEdit, onDelete, appModuleTabList }) => {
    return (
      <div className={styles.subList}>
        {items.map((item, index) => (
          <SubFunctionItem
            key={`sub-${index}`}
            index={index}
            itemIndex={index}
            item={item}
            groupIndex={groupIndex}
            group={group}
            onEdit={onEdit}
            onDelete={onDelete}
            appModuleTabList={appModuleTabList}
          />
        ))}
      </div>
    );
  },
);

const GroupItem = SortableElement(
  ({
    group,
    groupIndex,
    onAddSub,
    onEditSub,
    onDeleteSub,
    onDeleteGroup,
    onSubSortEnd,
    onEditGroupName,
    appModuleTabList,
  }) => {
    return (
      <Card
        className={styles.groupCard}
        title={
          <Space>
            <DragHandle />
            <span style={{ fontWeight: 'bold' }}>{group.moduleName}</span>
            <Button
              type="text"
              icon={<EditOutlined style={{ color: '#999' }} />}
              onClick={() => onEditGroupName(groupIndex, group.moduleName)}
              style={{ padding: '0 4px', height: 'auto' }}
            />
          </Space>
        }
        extra={
          <Space>
            {group.styleType !== 1 && (
              <Button type="link" onClick={() => onAddSub(groupIndex)}>
                添加子功能
              </Button>
            )}
            {
              <Popconfirm
                title="确定要删除此功能组吗？"
                onConfirm={() => onDeleteGroup(groupIndex)}
              >
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            }
          </Space>
        }
      >
        <SubFunctionList
          items={group.moduleTabList || []}
          groupIndex={groupIndex}
          group={group}
          onEdit={onEditSub}
          onDelete={onDeleteSub}
          onSortEnd={(sortData) => onSubSortEnd(sortData, groupIndex)}
          useDragHandle
          helperClass={styles.sortableHelper}
          appModuleTabList={appModuleTabList}
        />
      </Card>
    );
  },
);

const GroupList = SortableContainer(
  ({
    items,
    onAddSub,
    onEditSub,
    onDeleteSub,
    onDeleteGroup,
    onSubSortEnd,
    onEditGroupName,
    appModuleTabList,
  }) => {
    return (
      <div className={styles.groupList}>
        {items.map((group, index) => (
          <GroupItem
            key={`group-${index}`}
            index={index}
            groupIndex={index}
            group={group}
            onAddSub={onAddSub}
            onEditSub={onEditSub}
            onDeleteSub={onDeleteSub}
            onDeleteGroup={onDeleteGroup}
            onSubSortEnd={onSubSortEnd}
            onEditGroupName={onEditGroupName}
            appModuleTabList={appModuleTabList}
          />
        ))}
      </div>
    );
  },
);

const ConfigArea = ({
  moduleList,
  onChange,
  title,
  subTitle,
  onTitleChange,
  onSubTitleChange,
  appModuleTabList = [],
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isGroupModalVisible, setIsGroupModalVisible] = useState(false);
  const [editingIndices, setEditingIndices] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [form] = Form.useForm();
  const [groupForm] = Form.useForm();
  const [baseInfoForm] = Form.useForm();

  React.useEffect(() => {
    baseInfoForm.setFieldsValue({
      title,
      subTitle,
    });
  }, [title, subTitle, baseInfoForm]);

  const handleGroupSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;
    const newList = [...moduleList];
    const [moved] = newList.splice(oldIndex, 1);
    newList.splice(newIndex, 0, moved);
    onChange(newList);
  };

  const handleSubSortEnd = ({ oldIndex, newIndex }, groupIndex) => {
    if (oldIndex === newIndex) return;
    const newList = [...moduleList];
    const group = { ...newList[groupIndex] };
    const tabList = [...group.moduleTabList];

    const [moved] = tabList.splice(oldIndex, 1);
    tabList.splice(newIndex, 0, moved);

    group.moduleTabList = tabList;
    newList[groupIndex] = group;
    onChange(newList);
  };

  const handleAddGroup = () => {
    const newGroup = {
      moduleName: '新功能组',
      styleType: 0,
      moduleTabList: [],
    };
    onChange([...moduleList, newGroup]);
  };

  const handleDeleteGroup = (groupIndex) => {
    const newList = [...moduleList];
    newList.splice(groupIndex, 1);
    onChange(newList);
  };

  const handleAddSub = (groupIndex) => {
    setEditingIndices({ groupIndex, itemIndex: -1 });
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEditSub = (groupIndex, itemIndex, item) => {
    setEditingIndices({ groupIndex, itemIndex });
    form.setFieldsValue({
      tabName: item.tabName,
      tabIcon: item.tabIcon,
      jumpType: item.jumpType ?? 1,
      h5Url: item.h5Url,
      tabCode: item.tabCode,
      miniProgramAppId: item.miniProgramAppId,
      miniProgramPath: item.miniProgramPath,
    });
    setIsModalVisible(true);
  };

  const handleDeleteSub = (groupIndex, itemIndex) => {
    const newList = [...moduleList];
    const group = { ...newList[groupIndex] };
    const tabList = [...group.moduleTabList];

    tabList.splice(itemIndex, 1);
    group.moduleTabList = tabList;
    newList[groupIndex] = group;
    onChange(newList);
  };

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      const { groupIndex, itemIndex } = editingIndices;
      const newList = [...moduleList];
      const group = { ...newList[groupIndex] };
      const tabList = [...(group.moduleTabList || [])];

      const newItem = {
        tabName: values.tabName,
        tabIcon: values.tabIcon,
        jumpType: values.jumpType,
        h5Url: values.jumpType === 1 ? values.h5Url : null,
        tabCode: values.jumpType === 2 ? values.tabCode : null,
        miniProgramAppId:
          values.jumpType === 3 ? values.miniProgramAppId : null,
        miniProgramPath: values.jumpType === 3 ? values.miniProgramPath : null,
        popUpText: null,
        minVersion: null,
        maxVersion: null,
        popUpTitle: null,
      };

      if (itemIndex === -1) {
        tabList.push(newItem);
      } else {
        tabList[itemIndex] = { ...tabList[itemIndex], ...newItem };
      }

      group.moduleTabList = tabList;
      newList[groupIndex] = group;
      onChange(newList);
      setIsModalVisible(false);
    });
  };

  const handleEditGroupName = (groupIndex, currentName) => {
    setEditingGroup({ index: groupIndex });
    groupForm.setFieldsValue({ moduleName: currentName });
    setIsGroupModalVisible(true);
  };

  const handleGroupModalOk = () => {
    groupForm.validateFields().then((values) => {
      const newList = [...moduleList];
      newList[editingGroup.index] = {
        ...newList[editingGroup.index],
        moduleName: values.moduleName,
      };
      onChange(newList);
      setIsGroupModalVisible(false);
    });
  };

  return (
    <div className={styles.configContainer}>
      <Card title="页面基础信息" style={{ marginBottom: 16 }}>
        <Form form={baseInfoForm} style={{ display: 'flex', gap: '24px' }}>
          <Form.Item
            label="页面标题"
            name="title"
            style={{ flex: 1, marginBottom: 0 }}
            rules={[
              { required: true, message: '请输入页面标题' },
              {
                min: 2,
                max: 10,
                message: '页面标题长度必须在 2-10 个字符之间',
              },
            ]}
          >
            <Input
              onChange={onTitleChange}
              placeholder="请输入页面标题"
              maxLength={10}
            />
          </Form.Item>
          <Form.Item
            label="页面描述"
            name="subTitle"
            style={{ flex: 1, marginBottom: 0 }}
            rules={[
              { required: true, message: '请输入页面描述' },
              {
                min: 2,
                max: 30,
                message: '页面描述长度必须在 2-30 个字符之间',
              },
            ]}
          >
            <Input
              onChange={onSubTitleChange}
              placeholder="请输入页面描述"
              maxLength={30}
            />
          </Form.Item>
        </Form>
      </Card>

      <div className={styles.actionHeader}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddGroup}>
          新增功能组
        </Button>
      </div>

      <GroupList
        items={moduleList}
        onSortEnd={handleGroupSortEnd}
        useDragHandle
        helperClass={styles.sortableHelper}
        onAddSub={handleAddSub}
        onEditSub={handleEditSub}
        onDeleteSub={handleDeleteSub}
        onDeleteGroup={handleDeleteGroup}
        onSubSortEnd={handleSubSortEnd}
        onEditGroupName={handleEditGroupName}
        appModuleTabList={appModuleTabList}
      />

      <Modal
        title="编辑功能组名称"
        open={isGroupModalVisible}
        onOk={handleGroupModalOk}
        onCancel={() => setIsGroupModalVisible(false)}
        destroyOnClose
      >
        <Form form={groupForm} layout="vertical">
          <Form.Item
            label="组名称"
            name="moduleName"
            rules={[
              { required: true, message: '请输入功能组名称' },
              {
                min: 2,
                max: 18,
                message: '功能组名称长度必须在 2-18 个字符之间',
              },
            ]}
          >
            <Input placeholder="请输入功能组名称" maxLength={18} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingIndices?.itemIndex === -1 ? '添加子功能' : '编辑子功能'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="功能名称"
            name="tabName"
            rules={[
              { required: true, message: '请输入功能名称' },
              {
                min: 2,
                max: 18,
                message: '功能名称长度必须在 2-18 个字符之间',
              },
            ]}
          >
            <Input placeholder="请输入功能名称" maxLength={18} />
          </Form.Item>

          <Form.Item
            label="功能图标"
            name="tabIcon"
            rules={[{ required: true, message: '请上传功能图标' }]}
            extra="建议 120×120px PNG / JPG，大小不超过 1MB"
          >
            <FormImgUpload maxSize={1024 * 1024} />
          </Form.Item>

          <Form.Item
            label="跳转类型"
            name="jumpType"
            initialValue={1}
            rules={[{ required: true, message: '请选择跳转类型' }]}
          >
            <Radio.Group>
              <Radio value={1}>H5</Radio>
              <Radio value={2}>已有功能</Radio>
              <Radio value={3}>小程序</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.jumpType !== currentValues.jumpType
            }
          >
            {({ getFieldValue }) => {
              const jumpType = getFieldValue('jumpType');

              if (jumpType === 1) {
                return (
                  <Form.Item
                    label="目标页面"
                    name="h5Url"
                    rules={[{ required: true, message: '请输入目标链接' }]}
                  >
                    <Input placeholder="请输入目标链接" />
                  </Form.Item>
                );
              }

              if (jumpType === 2) {
                return (
                  <Form.Item
                    label="目标页面"
                    name="tabCode"
                    rules={[{ required: true, message: '请选择目标页面' }]}
                  >
                    <Select
                      showSearch
                      placeholder="请选择目标页面"
                      options={appModuleTabList.map((item) => ({
                        label: item.tabName,
                        value: item.tabCode,
                      }))}
                      filterOption={(input, option) =>
                        (option?.label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                );
              }

              if (jumpType === 3) {
                return (
                  <>
                    <Form.Item
                      label="小程序APP ID"
                      name="miniProgramAppId"
                      rules={[
                        { required: true, message: '请输入小程序APP ID' },
                      ]}
                    >
                      <Input placeholder="请输入小程序APP ID" />
                    </Form.Item>
                    <Form.Item
                      label="小程序路径"
                      name="miniProgramPath"
                      rules={[{ required: true, message: '请输入小程序路径' }]}
                    >
                      <Input placeholder="请输入小程序路径" />
                    </Form.Item>
                  </>
                );
              }
              return null;
            }}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ConfigArea;
